import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang, useT, useLocalePath } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { Calendar, Clock, MapPin, Euro, MessageCircleQuestion } from "lucide-react";
import { EventRow, evTitle, evDesc, fmtDate, fmtTime, fmtPrice, isAnnouncement, evDateNote } from "@/lib/events";

export const MagazinTabs = ({ active }: { active: "blog" | "events" }) => {
  const lp = useLocalePath();
  const t = useT({ a: "Artikel", e: "Veranstaltungen" }, { a: "Articles", e: "Events" });
  const cls = (on: boolean) =>
    `px-5 py-2 rounded-full text-sm font-medium transition-colors ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"}`;
  return (
    <div className="flex justify-center mb-8">
      <div className="inline-flex gap-1 p-1 rounded-full border border-border bg-card">
        <Link to={lp("/blog")} className={cls(active === "blog")}>{t.a}</Link>
        <Link to={lp("/veranstaltungen")} className={cls(active === "events")}>{t.e}</Link>
      </div>
    </div>
  );
};

const strip = (html: string) => html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const Veranstaltungen = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const t = useT(
    { eyebrow: "Magazin", h: "Veranstaltungen & Vorträge", intro: "Wissenschaftlich fundierte Vorträge zu Mikrobiom, Bakteriophagen, Antibiotikaresistenzen und mehr – verständlich aufbereitet.", empty: "Derzeit sind keine Veranstaltungen geplant. Schauen Sie bald wieder vorbei!", more: "Details & Anmeldung", seoT: "Veranstaltungen & Vorträge | Praxis Innere Balance", seoD: "Vorträge von Isabella Bartak zu Darmmikrobiom, Bakteriophagen und chronischen Beschwerden – Termine und Anmeldung." },
    { eyebrow: "Magazine", h: "Events & talks", intro: "Science-based talks on the microbiome, bacteriophages, antibiotic resistance and more – clearly explained.", empty: "No events are currently scheduled. Check back soon!", more: "Details & registration", seoT: "Events & talks | Praxis Innere Balance", seoD: "Talks by Isabella Bartak on the gut microbiome, bacteriophages and chronic conditions – dates and registration." },
  );
  useSeo({ title: t.seoT, description: t.seoD, canonical: "/veranstaltungen" });

  useEffect(() => {
    supabase.from("events").select("*").eq("published", true)
      .or(`starts_at.gt.${new Date().toISOString()},starts_at.is.null`)
      .order("starts_at", { ascending: true, nullsFirst: false })
      .then(({ data }) => setEvents((data as unknown as EventRow[]) ?? []));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">{t.eyebrow}</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">{t.h}</h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">{t.intro}</p>
          </div>
          <MagazinTabs active="events" />
          {events === null ? null : events.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">{t.empty}</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {events.map((e) => (
                <Link key={e.id} to={lp(`/veranstaltungen/${e.slug}`)} className="group rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
                  {e.cover_image_url && <img src={e.cover_image_url} alt="" loading="lazy" className="aspect-video object-cover w-full" />}
                  <div className="p-5 flex flex-col flex-1">
                    {isAnnouncement(e) && (
                      <span className="self-start mb-2 rounded-full bg-secondary px-3 py-0.5 text-xs font-medium text-secondary-foreground">
                        {lang === "en" ? "Coming soon" : "Demnächst"}
                      </span>
                    )}
                    <h2 className="text-lg font-semibold text-foreground group-hover:text-primary mb-2">{evTitle(e, lang)}</h2>
                    {(e.speakers ?? []).length > 0 && (
                      <p className="text-xs font-medium text-primary mb-2">
                        {lang === "en" ? "Guest: " : "Gast: "}
                        {(e.speakers ?? []).map((sp) => `${sp.title} ${sp.name}`.trim() + (sp.specialty ? ` (${(lang === "en" && sp.specialty_en) || sp.specialty})` : "")).join(", ")}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{strip(evDesc(e, lang))}</p>
                    <ul className="text-sm text-muted-foreground space-y-1 mt-auto">
                      {e.starts_at ? (<>
                        <li className="flex gap-2"><Calendar className="w-4 h-4 text-primary" />{fmtDate(e.starts_at, lang)}</li>
                        <li className="flex gap-2"><Clock className="w-4 h-4 text-primary" />{fmtTime(e.starts_at, lang)} · {e.duration_minutes} Min.</li>
                      </>) : (
                        <li className="flex gap-2"><Calendar className="w-4 h-4 text-primary" />{evDateNote(e, lang)}</li>
                      )}
                      {e.location && <li className="flex gap-2"><MapPin className="w-4 h-4 text-primary" />{e.location}</li>}
                      <li className="flex gap-2"><Euro className="w-4 h-4 text-primary" />{fmtPrice(e.price_eur, lang)}</li>
                      {e.qa && <li className="flex gap-2"><MessageCircleQuestion className="w-4 h-4 text-primary" />{lang === "en" ? "With Q&A session" : "Mit Fragerunde"}</li>}
                    </ul>
                    <span className="mt-4 text-sm font-medium text-primary">{t.more} →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Veranstaltungen;
